import type { OrderUnifiedOutput } from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import OrdersSingleAction from "@/pages/orders/OrdersSingleAction";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useGetBuyerOrders, useGetSellerOrders } from "@/services";
import { getTimeSinceText } from "@/utils/timestamp";
import {
	calculateTaxPercentage,
	formatNumber,
	getOrderEmail,
} from "@/utils/tools";
import { useOrderStatusTranslator } from "@/utils/translations";
import {
	Button,
	Chip,
	ErrorBoundary,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import type { FC, ReactNode } from "react";
import {
	DashboardTable,
	EmptyRow,
	LoadingRows,
	STATUS_COLOR_TO_CHIP_VARIANT,
	SectionCard,
} from "./DashboardPrimitives";

export const DashboardRecentOrders: FC<{
	side: "buyer" | "seller";
	title: string;
	action?: ReactNode;
	limit?: number;
}> = ({ side, title, action, limit = 5 }) => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const navigate = useNavigate();
	const translateStatus = useOrderStatusTranslator(t);

	const isBuyer = side === "buyer";
	const query = { page: 1, page_size: limit };

	const buyerOrders = useGetBuyerOrders({ query }, { enabled: isBuyer });
	const sellerOrders = useGetSellerOrders({ query }, { enabled: !isBuyer });

	const { data, isLoading, error, refetch } = isBuyer
		? buyerOrders
		: sellerOrders;
	const orders = (data?.results ?? []) as OrderUnifiedOutput[];
	// Sellers also see the tax they took on each order.
	const columns = isBuyer ? 7 : 8;
	const tableError = error ? getErrorInterface(error) : undefined;
	if (tableError) {
		return (
			<SectionCard title={title} viewAllTo="/orders" action={action}>
				<ErrorBoundary isLoading={false} error={tableError} onReset={refetch}>
					<div />
				</ErrorBoundary>
			</SectionCard>
		);
	}

	return (
		<SectionCard title={title} viewAllTo="/orders" action={action}>
			<DashboardTable>
				<TableHeader>
					<TableRow>
						<TableHead>{t("order.tableOrderEmail")}</TableHead>
						<TableHead>
							{isBuyer ? t("order.tableSeller") : t("order.tableBuyer")}
						</TableHead>
						<TableHead>{t("order.tableDeliveryAmount")}</TableHead>
						{isBuyer ? null : <TableHead>{t("order.tableTax")}</TableHead>}
						<TableHead>{t("order.tableTotalPrice")}</TableHead>
						<TableHead>{t("order.tableStatus")}</TableHead>
						<TableHead>{t("order.tableDate")}</TableHead>
						<TableHead />
					</TableRow>
				</TableHeader>
				<TableBody>
					{isLoading ? (
						<LoadingRows columns={columns} rows={3} />
					) : orders.length === 0 ? (
						<EmptyRow colSpan={columns}>{t("home.noOrders")}</EmptyRow>
					) : (
						orders.map((order) => {
							const { text, color } = translateStatus(order.status);
							return (
								<TableRow key={order.pk}>
									<TableCell data-label={t("order.tableOrderEmail")}>
										<Button
											type="button"
											variant="text"
											color="primary"
											size="sm"
											className="h-auto max-w-full whitespace-normal break-all text-start"
											onClick={() => navigate({ to: `/orders/${order.pk}` })}
										>
											{getOrderEmail(order)}
										</Button>
									</TableCell>
									<TableCell
										data-label={
											isBuyer ? t("order.tableSeller") : t("order.tableBuyer")
										}
									>
										<RelationNameLink
											name={isBuyer ? order.seller_name : order.buyer_name}
											relationId={order.relation_id}
											party={isBuyer ? "seller" : "buyer"}
										/>
									</TableCell>
									<TableCell data-label={t("order.tableDeliveryAmount")}>
										{`${formatNumber(
											order.delivered / 1000,
											"count",
										)} / ${formatNumber(order.amount / 1000, "count")}${t(
											"signs.thousands",
										)}`}
									</TableCell>
									{isBuyer ? null : (
										<TableCell data-label={t("order.tableTax")}>
											{order.delivered
												? formatNumber(
														calculateTaxPercentage(
															order.delivered,
															"taken" in order ? order.taken : 0,
														),
														"percent",
													)
												: "-"}
										</TableCell>
									)}
									<TableCell data-label={t("order.tableTotalPrice")}>
										{formatNumber(order.total_price, "price")}
									</TableCell>
									<TableCell data-label={t("order.tableStatus")}>
										<Chip
											variant={STATUS_COLOR_TO_CHIP_VARIANT[color]}
											size="sm"
											className="whitespace-nowrap"
										>
											{text}
										</Chip>
									</TableCell>
									<TableCell data-label={t("order.tableDate")}>
										{(order.created_date &&
											getTimeSinceText(order.created_date, t, lang)) ||
											"-"}
									</TableCell>
									<TableCell className="text-end">
										<OrdersSingleAction selectedOrder={order} />
									</TableCell>
								</TableRow>
							);
						})
					)}
				</TableBody>
			</DashboardTable>
		</SectionCard>
	);
};
